import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMyVehicles } from "@/hooks/useVehicles";
import { useEvents } from "@/hooks/useEvents";
import { useTranslation } from "react-i18next";
import type { CreateSessionRequest, Session } from "@/types";

interface SessionFormProps {
  onSubmit: (data: CreateSessionRequest) => void;
  onCancel: () => void;
  initialData?: Session;
  isLoading?: boolean;
  mode?: "create" | "edit";
}

type FormData = {
  vehicleId: string;
  eventId: string;
  title: string;
  description?: string;
  visibility: "public" | "team_members" | "private";
  leaderboardOptIn: boolean;
  conductedAt: string;
};

function dedupeById<T extends { id: string }>(
  items: Array<T | undefined | null>,
): T[] {
  const seen = new Set<string>();
  const result: T[] = [];
  for (const item of items) {
    if (item && !seen.has(item.id)) {
      seen.add(item.id);
      result.push(item);
    }
  }
  return result;
}

export default function SessionForm({
  onSubmit,
  onCancel,
  initialData,
  isLoading,
  mode = initialData ? "edit" : "create",
}: SessionFormProps) {
  const { t } = useTranslation(["pages", "common"]);
  const { data: vehicles = [], isLoading: vehiclesLoading } = useMyVehicles();
  const { data: eventsData, isLoading: eventsLoading } = useEvents();
  const events = Array.isArray(eventsData)
    ? eventsData
    : eventsData?.events || [];

  // Keep the session's current values selectable (e.g. while lists load)
  const availableVehicles = dedupeById([initialData?.vehicle, ...vehicles]);
  const availableEvents = dedupeById([initialData?.event, ...events]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    defaultValues: initialData
      ? {
          vehicleId:
            initialData.vehicleId || initialData.vehicle?.id || "",
          eventId: initialData.eventId || initialData.event?.id || "",
          title: initialData.title,
          description: initialData.description || "",
          visibility: initialData.visibility || "private",
          leaderboardOptIn: initialData.leaderboardOptIn ?? false,
          conductedAt: initialData.conductedAt
            ? initialData.conductedAt.split("T")[0]
            : "",
        }
      : {
          title: "",
          visibility: "private",
          leaderboardOptIn: false,
          conductedAt: new Date().toISOString().split("T")[0], // Default to today
        },
  });

  const selectedEventId = watch("eventId");
  const selectedEvent = availableEvents.find((e) => e.id === selectedEventId);

  useEffect(() => {
    if (initialData) {
      // Reset form with initial data whenever initialData changes
      reset({
        vehicleId:
          initialData.vehicleId || initialData.vehicle?.id || "",
        eventId: initialData.eventId || initialData.event?.id || "",
        title: initialData.title,
        description: initialData.description || "",
        visibility: initialData.visibility || "private",
        leaderboardOptIn: initialData.leaderboardOptIn ?? false,
        conductedAt: initialData.conductedAt
          ? initialData.conductedAt.split("T")[0]
          : "",
      });
    }
  }, [initialData, reset]);

  const handleFormSubmit = (data: FormData) => {
    // Convert conductedAt to ISO format
    const conductedAt = new Date(data.conductedAt).toISOString();
    onSubmit({
      ...data,
      conductedAt,
    });
  };

  const isDataLoading = vehiclesLoading || eventsLoading;

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
 className="space-y-4 sm:space-y-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div>
          <label
            htmlFor="eventId"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:sessions.form.event")} *
          </label>
          <select
            id="eventId"
            {...register("eventId", {
              required: t("pages:sessions.form.eventRequired"),
            })}
            className="input"
            disabled={isDataLoading}
          >
            <option value="">{t("pages:sessions.form.selectEvent")}</option>
            {availableEvents.map((event) => (
              <option key={event.id} value={event.id}>
                {event.name}
              </option>
            ))}
          </select>
          {errors.eventId && (
            <p className="mt-1 text-sm text-red-600">
              {errors.eventId.message}
            </p>
          )}
          {selectedEvent?.track && (
            <p className="mt-1 text-sm text-secondary-500">
              {t("pages:sessions.form.eventTrack")}: {selectedEvent.track.name}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="vehicleId"
            className="block text-sm font-medium text-secondary-700 mb-2"
          >
            {t("pages:sessions.form.vehicle")} *
          </label>
          <select
            id="vehicleId"
            {...register("vehicleId", {
              required: t("pages:sessions.form.vehicleRequired"),
            })}
            className="input"
            disabled={isDataLoading}
          >
            <option value="">{t("pages:sessions.form.selectVehicle")}</option>
            {availableVehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.name ||
                  `${vehicle.year} ${vehicle.maker} ${vehicle.model}`}
              </option>
            ))}
          </select>
          {errors.vehicleId && (
            <p className="mt-1 text-sm text-red-600">
              {errors.vehicleId.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="conductedAt"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("pages:sessions.form.date")} *
        </label>
        <input
          type="date"
          id="conductedAt"
          {...register("conductedAt", {
            required: t("pages:sessions.form.dateRequired"),
          })}
          className="input"
        />
        {errors.conductedAt && (
          <p className="mt-1 text-sm text-red-600">
            {errors.conductedAt.message}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="visibility"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("pages:sessions.form.visibility")}
        </label>
        <select
          id="visibility"
          {...register("visibility")}
          className="input"
        >
          <option value="private">
            {t("pages:sessions.form.visibilityPrivate")}
          </option>
          <option value="team_members">
            {t("pages:sessions.form.visibilityTeam")}
          </option>
          <option value="public">
            {t("pages:sessions.form.visibilityPublic")}
          </option>
        </select>
        <p className="mt-1 text-sm text-secondary-500">
          {t("pages:sessions.form.visibilityHelp")}
        </p>
      </div>

      <div>
        <label
          htmlFor="leaderboardOptIn"
          className="flex items-center gap-2 text-sm font-medium text-secondary-700"
        >
          <input
            type="checkbox"
            id="leaderboardOptIn"
            {...register("leaderboardOptIn")}
            className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
          />
          {t("pages:sessions.form.leaderboardOptIn")}
        </label>
        <p className="mt-1 text-sm text-secondary-500">
          {t("pages:sessions.form.leaderboardOptInHelp")}
        </p>
      </div>

      <div className="sm:col-span-2 lg:col-span-3">
        <label
          htmlFor="title"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("pages:sessions.form.title")} *
        </label>
        <input
          type="text"
          id="title"
          {...register("title", {
            required: t("pages:sessions.form.titleRequired"),
          })}
          className="input"
          placeholder={t("pages:sessions.form.titlePlaceholder")}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      <div className="sm:col-span-2 lg:col-span-3">
        <label
          htmlFor="description"
          className="block text-sm font-medium text-secondary-700 mb-2"
        >
          {t("common:description")}
        </label>
        <textarea
          id="description"
          rows={3}
          {...register("description")}
          className="input resize-none"
          placeholder={t("pages:sessions.form.descriptionPlaceholder")}
        />
      </div>

      {/* Responsive status messages */}
      {isDataLoading && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 sm:p-4 sm:col-span-2 lg:col-span-3">
          <p className="text-primary-800 text-sm">
            {t("pages:sessions.form.loadingVehiclesTracks")}
          </p>
        </div>
      )}

      {mode === "create" &&
        availableVehicles.length === 0 &&
        !vehiclesLoading && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 sm:col-span-2 lg:col-span-3">
            <p className="text-yellow-800 text-sm">
              {t("pages:sessions.form.noVehicles")}
            </p>
          </div>
        )}

      {mode === "create" && availableEvents.length === 0 && !eventsLoading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 sm:col-span-2 lg:col-span-3">
          <p className="text-yellow-800 text-sm">
            {t("pages:sessions.form.noEvents")}
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-6 border-t border-secondary-200 sm:col-span-2 lg:col-span-3">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary px-4 py-2 w-full sm:w-auto order-2 sm:order-1"
          disabled={isLoading}
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          className="btn-primary px-4 py-2 w-full sm:w-auto order-1 sm:order-2"
          disabled={
            isLoading ||
            isDataLoading ||
            (mode === "create" &&
              (availableVehicles.length === 0 ||
                availableEvents.length === 0))
          }
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {initialData
                ? t("common:updating")
                : t("common:creating")}
            </div>
          ) : initialData ? (
            t("pages:sessions.form.update")
          ) : (
            t("pages:sessions.form.create")
          )}
        </button>
      </div>
    </form>
  );
}
